import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Download, Trash2, Users, PenSquare, History } from 'lucide-react';
import { format } from 'date-fns';
import { Button } from '@/components/ui/button';
import NewsletterComposer from '@/components/admin/NewsletterComposer';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminSubscribers = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [exporting, setExporting] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const [history, setHistory] = useState([]);
  const authConfig = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` } });

  useEffect(() => {
    fetchSubscribers();
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/newsletter/history`, authConfig());
      setHistory(data);
    } catch (error) {
      console.error('Error fetching history:', error);
    }
  };

  const fetchSubscribers = async () => {
    try {
      const { data } = await axios.get(`${API}/admin/subscribers`, authConfig());
      setSubscribers(data);
    } catch (error) {
      console.error('Error fetching subscribers:', error);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/subscribers/export`, { ...authConfig(), responseType: 'blob' });
      const url = window.URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vocavi-subscribers-${format(new Date(), 'yyyyMMdd')}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success('CSV downloaded');
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this subscriber?')) return;
    try {
      await axios.delete(`${API}/admin/subscribers/${id}`, authConfig());
      toast.success('Subscriber removed');
      fetchSubscribers();
    } catch (error) {
      toast.error('Failed to remove subscriber');
    }
  };

  return (
    <div data-testid="admin-subscribers-page">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-primary">Newsletter Subscribers</h1>
          <p className="text-muted-foreground text-sm mt-1 flex items-center gap-2" data-testid="subscribers-count">
            <Users size={16} /> {subscribers.length} total
          </p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleExport} disabled={exporting || subscribers.length === 0} data-testid="export-subscribers-button">
            <Download className="mr-2" size={18} /> {exporting ? 'Exporting...' : 'Export CSV'}
          </Button>
          <Button onClick={() => setComposerOpen(true)} data-testid="compose-newsletter-button">
            <PenSquare className="mr-2" size={18} /> Compose Newsletter
          </Button>
        </div>
      </div>

      <NewsletterComposer
        open={composerOpen}
        onOpenChange={setComposerOpen}
        subscriberCount={subscribers.length}
        onSent={fetchHistory}
      />

      <div className="bg-white shadow-md overflow-x-auto">
        <table className="w-full">
          <thead className="bg-muted">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Email</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Source</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Subscribed</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {subscribers.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-6 py-12 text-center text-muted-foreground" data-testid="subscribers-empty">
                  No subscribers yet.
                </td>
              </tr>
            ) : (
              subscribers.map((s) => (
                <tr key={s.id} data-testid={`subscriber-row-${s.id}`}>
                  <td className="px-6 py-4 text-sm font-medium text-primary">{s.email}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground capitalize">{s.source}</td>
                  <td className="px-6 py-4 text-sm text-muted-foreground">
                    {format(new Date(s.subscribed_at), 'MMM dd, yyyy HH:mm')}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <button onClick={() => handleDelete(s.id)} data-testid={`delete-subscriber-${s.id}`} className="text-red-600 hover:text-red-800">
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="mt-12" data-testid="newsletter-history">
        <h2 className="text-xl font-bold text-primary mb-4 flex items-center gap-2">
          <History size={20} /> Sent Newsletters
        </h2>
        <div className="bg-white shadow-md overflow-x-auto">
          <table className="w-full">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Subject</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Recipients</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Delivered</th>
                <th className="px-6 py-3 text-left text-xs font-semibold text-primary uppercase">Sent</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200">
              {history.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-6 py-10 text-center text-muted-foreground" data-testid="newsletter-history-empty">
                    No newsletters sent yet.
                  </td>
                </tr>
              ) : (
                history.map((c) => (
                  <tr key={c.id} data-testid={`campaign-row-${c.id}`}>
                    <td className="px-6 py-4 text-sm font-medium text-primary">{c.subject}</td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">{c.recipients}</td>
                    <td className="px-6 py-4 text-sm">
                      <span className={`px-2 py-1 text-xs font-semibold ${c.failed ? 'bg-amber-100 text-amber-800' : 'bg-green-100 text-green-800'}`}>
                        {c.sent} sent{c.failed ? `, ${c.failed} failed` : ''}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-muted-foreground">
                      {format(new Date(c.sent_at), 'MMM dd, yyyy HH:mm')} · {c.sent_by}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminSubscribers;
